import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import { EditButtons, ProductCategoryInput } from '@barboxx/admin-components';
import { ALCOHOL_TYPE_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ProductType } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    productType: ProductType.DetailsResponse;
}

export const ProductTypeDetailsCard = ({ productType }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutateUser = useMutation({
        mutationKey: ['productType', productType.id],
        mutationFn: (data: ProductType.UpdateRequest) =>
            Api.Products.Types.putProductType(data),
    });

    const formContext = useForm<ProductType.UpdateRequest>({
        resolver: yupResolver(ALCOHOL_TYPE_SCHEMA),
        defaultValues: productType,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: ProductType.UpdateRequest) => {
        mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated product brand!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating product brand!', {
                    severity: 'error',
                });
            });
    };

    return (
        <Card>
            <CardHeader title="Alcohol Details" />
            <CardContent>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                        <TextFieldElement
                            fullWidth
                            name="abvRange"
                            disabled={!editable}
                            label="ABV Range"
                        />
                        <TextFieldElement
                            fullWidth
                            name="productionMethod"
                            disabled={!editable}
                            label="Production Method"
                        />
                        <ProductCategoryInput disabled={!editable} />
                        <CheckboxElement
                            name="isAlcoholic"
                            label="Is Alcoholic"
                            disabled={!editable}
                        />

                        <Can I="update" a="Alcohol">
                            <EditButtons
                                formContext={formContext}
                                handleCancel={handleCancel}
                                editable={editable}
                                editableActions={editableActions}
                            />
                        </Can>
                    </Stack>
                </FormContainer>
            </CardContent>
        </Card>
    );
};
